import PropTypes from 'prop-types';
import { useEffect, useRef, useState } from 'react';
import MasterDataTableCombobox from './MasterDataTableCombobox';

/**
 * Authoring UI for the `masterDataMappings` top-level schema key.
 *
 * Production shape (audit of admin_buildex_dev — 17/17 real DEs):
 *   {
 *     [fieldName]: {
 *       masterDataTableName: string,
 *       masterDataFieldName: string,
 *     }
 *   }
 *
 * Editor state model:
 *   - Local array of rows with stable UUIDs (never derived from fieldName,
 *     because deriving from a value that changes on every keystroke would
 *     remount the input and drop focus on every character).
 *   - Blank rows are kept in local state even though `emit()` filters
 *     them out of the schema-shape value. Without this, "+ Add Mapping"
 *     would appear to do nothing (the new blank row would never reach the
 *     schema, so a value-derived rows array would never include it).
 *   - When the parent's value changes from the outside (e.g. a different
 *     DE loads), reseed local state from the new value.
 */

let __rowCounter = 0;
function newRowId() {
	__rowCounter += 1;
	return `mdm-${Date.now()}-${__rowCounter}`;
}

function valueToRows(value) {
	if (!value || typeof value !== 'object') return [];
	return Object.entries(value).map(([fieldName, mapping]) => ({
		id: newRowId(),
		fieldName,
		masterDataTableName: mapping?.masterDataTableName ?? '',
		masterDataFieldName: mapping?.masterDataFieldName ?? '',
	}));
}

function rowsToValue(rows) {
	const out = {};
	for (const r of rows) {
		const key = (r.fieldName || '').trim();
		if (!key) continue;
		out[key] = {
			masterDataTableName: r.masterDataTableName ?? '',
			masterDataFieldName: r.masterDataFieldName ?? '',
		};
	}
	return Object.keys(out).length > 0 ? out : null;
}

export default function MasterDataMappingsForm({
	value,
	onChange,
	disabled = false,
	fieldOptions = [],
	tableOptions = [],
}) {
	const [rows, setRows] = useState(() => valueToRows(value));
	// Track the value reference we've already synced so external resets
	// (DE switch, JSON upload) reseed local state without overwriting an
	// in-progress edit on every render.
	const lastSeenValueRef = useRef(value);

	useEffect(() => {
		if (value === lastSeenValueRef.current) return;
		// Re-emit-from-rows would equal the incoming value if we caused this
		// change ourselves; only reseed when the change came from outside.
		const ourLastEmit = rowsToValue(rows);
		const sameAsRows = JSON.stringify(ourLastEmit) === JSON.stringify(value);
		lastSeenValueRef.current = value;
		if (!sameAsRows) {
			setRows(valueToRows(value));
		}
	}, [value, rows]);

	const commit = (nextRows) => {
		setRows(nextRows);
		const next = rowsToValue(nextRows);
		lastSeenValueRef.current = next;
		onChange(next);
	};

	const update = (id, patch) => {
		commit(rows.map((r) => (r.id === id ? { ...r, ...patch } : r)));
	};

	const add = () => {
		commit([
			...rows,
			{ id: newRowId(), fieldName: '', masterDataTableName: '', masterDataFieldName: '' },
		]);
	};

	const remove = (id) => {
		commit(rows.filter((r) => r.id !== id));
	};

	const fieldListId = 'cv-mdm-field-options';

	return (
		<div className="dsv-cv-form" data-testid="cv-mdm-form">
			<p className="dsv-cv-form__hint">
				Bind a field on this Data Element to a column on a master-data
				table so the runtime can validate and auto-populate values.
			</p>

			<datalist id={fieldListId}>
				{fieldOptions.map((opt) => <option key={opt} value={opt} />)}
			</datalist>

			{rows.length === 0 && (
				<p className="dsv-cv-form__empty" data-testid="cv-mdm-empty">
					No mappings yet.
				</p>
			)}

			{rows.length > 0 && (
				<div className="dsv-cv-table dsv-cv-table--mdm" role="table">
					<div className="dsv-cv-table__head" role="row">
						<div className="dsv-cv-table__cell" role="columnheader">Field Name</div>
						<div className="dsv-cv-table__cell" role="columnheader">Master Data Table</div>
						<div className="dsv-cv-table__cell" role="columnheader">Master Data Column</div>
						<div className="dsv-cv-table__cell dsv-cv-table__cell--actions" role="columnheader" aria-label="Actions" />
					</div>
					{rows.map((row) => (
						<div key={row.id} className="dsv-cv-table__row" role="row" data-testid="cv-mdm-row">
							<div className="dsv-cv-table__cell" role="cell">
								<input
									className="dsv-modal__input"
									type="text"
									list={fieldListId}
									value={row.fieldName}
									disabled={disabled}
									onChange={(e) => update(row.id, { fieldName: e.target.value })}
									placeholder="e.g. project_reference_number"
								/>
							</div>
							<div className="dsv-cv-table__cell" role="cell">
								<MasterDataTableCombobox
									value={row.masterDataTableName}
									onChange={(next) => update(row.id, { masterDataTableName: next })}
									options={tableOptions}
									disabled={disabled}
									placeholder="e.g. a-i-project-master-data"
								/>
							</div>
							<div className="dsv-cv-table__cell" role="cell">
								<input
									className="dsv-modal__input"
									type="text"
									value={row.masterDataFieldName}
									disabled={disabled}
									onChange={(e) => update(row.id, { masterDataFieldName: e.target.value })}
									placeholder="e.g. project_reference_number"
								/>
							</div>
							<div className="dsv-cv-table__cell dsv-cv-table__cell--actions" role="cell">
								{!disabled && (
									<button
										type="button"
										className="dsv-cv-table__remove"
										onClick={() => remove(row.id)}
										data-testid="cv-mdm-remove"
										aria-label="Remove mapping"
										title="Remove mapping"
									>
										×
									</button>
								)}
							</div>
						</div>
					))}
				</div>
			)}

			{!disabled && (
				<button
					type="button"
					className="dsv-btn dsv-btn--secondary dsv-cv-form__add"
					onClick={add}
					data-testid="cv-mdm-add"
				>
					+ Add Mapping
				</button>
			)}
		</div>
	);
}

MasterDataMappingsForm.propTypes = {
	value: PropTypes.object,
	onChange: PropTypes.func.isRequired,
	disabled: PropTypes.bool,
	fieldOptions: PropTypes.arrayOf(PropTypes.string),
	tableOptions: PropTypes.arrayOf(PropTypes.string),
};
